import React, { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../../components/ui/PageContainer';
import { useHasFeature } from '../../../store/featureFlagsStore';
import { api, ApiError } from '../../../utils/api';
import { showToast } from '../../../utils/toast';
import { Eyebrow } from '../parts/primitives';

type QuestionType = 'text' | 'scale' | 'select' | 'multi-select';

interface Question {
  id?: string;
  text: string;
  type: QuestionType;
  required?: boolean;
  anonymous?: boolean;
  allowDeanonymize?: boolean;
  scaleMin?: number;
  scaleMax?: number;
  scaleLabels?: string[];
  options?: string[];
  block?: string;
}

interface Questionnaire {
  _id: string;
  name: string;
  selfForm: { questions: Question[] };
  peerExtraQuestions?: Question[];
}

interface Draft {
  _id?: string;
  name: string;
  self: Question[];
  peer: Question[];
}

const LIST_KEY = ['/performance-review/questionnaires'];

const toDraft = (q: Questionnaire): Draft => ({
  _id: q._id,
  name: q.name,
  self: q.selfForm?.questions ?? [],
  peer: q.peerExtraQuestions ?? [],
});

const blankQuestion = (): Question => ({ text: '', type: 'text', required: false });

// Конструктор анкет Performance Review (бета). Самооценка задаётся целиком;
// анкету коллег собирает карта компетенций, здесь к ней только добавляют вопросы.
export const PerformanceReviewQuestionnaires: React.FC = () => {
  const { t } = useTranslation();
  const isOn = useHasFeature('questionnaireBuilder');
  const queryClient = useQueryClient();
  const { data: list = [], isLoading } = useQuery({
    queryKey: LIST_KEY,
    queryFn: (): Promise<Questionnaire[]> => api('/performance-review/questionnaires'),
    enabled: isOn,
  });
  const [draft, setDraft] = useState<Draft | null>(null);

  // Открыть первую анкету, когда список пришёл и ничего не выбрано.
  useEffect(() => {
    if (!draft && list.length) setDraft(toDraft(list[0]));
  }, [draft, list]);

  const refresh = () =>
    queryClient.invalidateQueries({
      predicate: (q) => String(q.queryKey[0]).startsWith('/performance-review/questionnaires'),
    });

  const save = useMutation({
    mutationFn: (d: Draft): Promise<Questionnaire> =>
      api(`/performance-review/questionnaires${d._id ? `/${d._id}` : ''}`, {
        method: d._id ? 'PUT' : 'POST',
        body: {
          name: d.name.trim(),
          selfForm: { questions: d.self },
          peerExtraQuestions: d.peer,
        },
      }),
    onSuccess: (saved) => {
      setDraft(toDraft(saved));
      refresh();
      showToast({ title: t('perfReview.questionnaires.saved'), message: '', color: 'teal' });
    },
    onError: (e) =>
      showToast({
        title: t('common.error'),
        message:
          e instanceof ApiError && e.status === 409
            ? t('perfReview.questionnaires.nameTaken')
            : t('perfReview.questionnaires.saveFailed'),
        type: 'error',
      }),
  });

  const archive = useMutation({
    mutationFn: (id: string) =>
      api(`/performance-review/questionnaires/${id}`, { method: 'DELETE' }),
    // Сначала свежий список, потом сброс: иначе автовыбор откроет архивную из старого кэша.
    onSuccess: async () => {
      await refresh();
      setDraft(null);
    },
    onError: () =>
      showToast({
        title: t('common.error'),
        message: t('perfReview.questionnaires.archiveFailed'),
        type: 'error',
      }),
  });

  if (!isOn) return <Navigate to="/performance-review/admin" replace />;

  const confirmArchive = (d: Draft) =>
    modals.openConfirmModal({
      title: t('perfReview.questionnaires.archiveTitle', { name: d.name }),
      children: <Text size="sm">{t('perfReview.questionnaires.archiveText')}</Text>,
      labels: { confirm: t('perfReview.questionnaires.archive'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => d._id && archive.mutate(d._id),
    });

  const isValid =
    !!draft?.name.trim() && [...draft.self, ...draft.peer].every((q) => q.text.trim());

  return (
    <PageContainer>
      <Stack gap="xl">
        <Group justify="space-between" align="flex-end">
          <Box>
            <Eyebrow>{t('perfReview.adminHome.eyebrow')}</Eyebrow>
            <Title order={2} mt={6}>
              {t('perfReview.questionnaires.title')}
            </Title>
          </Box>
          <Button
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDraft({ name: '', self: [blankQuestion()], peer: [] })}
          >
            {t('perfReview.questionnaires.create')}
          </Button>
        </Group>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 4 }}>
            {isLoading ? (
              <Loader size="sm" />
            ) : (
              <Stack gap={4}>
                {list.map((q) => {
                  const isSelected = draft?._id === q._id;
                  return (
                    <UnstyledButton
                      key={q._id}
                      onClick={() => setDraft(toDraft(q))}
                      aria-current={isSelected || undefined}
                      p="sm"
                      style={{
                        borderRadius: 'var(--mantine-radius-md)',
                        background: isSelected ? 'var(--mantine-color-default-hover)' : undefined,
                      }}
                    >
                      <Text size="sm" fw={500}>
                        {q.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {t('perfReview.questionnaires.counts', {
                          self: q.selfForm?.questions?.length ?? 0,
                          peer: q.peerExtraQuestions?.length ?? 0,
                        })}
                      </Text>
                    </UnstyledButton>
                  );
                })}
              </Stack>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8 }}>
            {draft && (
              <Stack gap="lg">
                <TextInput
                  label={t('perfReview.questionnaires.name')}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
                  maxLength={120}
                  required
                />
                <QuestionList
                  title={t('perfReview.questionnaires.selfTitle')}
                  hint={t('perfReview.questionnaires.selfHint')}
                  questions={draft.self}
                  onChange={(self) => setDraft({ ...draft, self })}
                  kind="self"
                />
                <QuestionList
                  title={t('perfReview.questionnaires.peerTitle')}
                  hint={t('perfReview.questionnaires.peerHint')}
                  questions={draft.peer}
                  onChange={(peer) => setDraft({ ...draft, peer })}
                  kind="peer"
                />
                <Group justify="space-between">
                  <Group gap="xs">
                    {draft._id && (
                      <Button
                        variant="subtle"
                        onClick={() =>
                          setDraft({
                            ...draft,
                            _id: undefined,
                            name: t('perfReview.questionnaires.copyName', { name: draft.name }),
                          })
                        }
                      >
                        {t('perfReview.questionnaires.copy')}
                      </Button>
                    )}
                    {draft._id && (
                      <Button variant="subtle" color="red" onClick={() => confirmArchive(draft)}>
                        {t('perfReview.questionnaires.archive')}
                      </Button>
                    )}
                  </Group>
                  <Button
                    onClick={() => save.mutate(draft)}
                    loading={save.isPending}
                    disabled={!isValid}
                  >
                    {t('perfReview.questionnaires.save')}
                  </Button>
                </Group>
              </Stack>
            )}
          </Grid.Col>
        </Grid>
      </Stack>
    </PageContainer>
  );
};

interface QuestionListProps {
  title: string;
  hint: string;
  questions: Question[];
  onChange: (next: Question[]) => void;
  kind: 'self' | 'peer';
}

const QuestionList: React.FC<QuestionListProps> = ({ title, hint, questions, onChange, kind }) => {
  const { t } = useTranslation();
  const update = (i: number, patch: Partial<Question>) =>
    onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i: number, by: number) => {
    const next = [...questions];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onChange(next);
  };

  return (
    <Stack gap="xs">
      <Box>
        <Text fw={600}>{title}</Text>
        <Text size="sm" c="dimmed">
          {hint}
        </Text>
      </Box>
      {questions.map((q, i) => {
        const isChoice = q.type === 'select' || q.type === 'multi-select';
        return (
          <Paper key={q.id ?? `new-${i}`} withBorder p="sm" radius="md">
            <Stack gap="xs">
              <Group align="flex-start" wrap="nowrap" gap="xs">
                <Textarea
                  aria-label={t('perfReview.questionnaires.questionText', { n: i + 1 })}
                  placeholder={t('perfReview.questionnaires.questionPlaceholder')}
                  value={q.text}
                  onChange={(e) => update(i, { text: e.currentTarget.value })}
                  autosize
                  minRows={1}
                  maxLength={500}
                  style={{ flex: 1 }}
                />
                <Group gap={2} wrap="nowrap">
                  <Tooltip label={t('perfReview.questionnaires.up')}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                      aria-label={t('perfReview.questionnaires.up')}
                    >
                      <IconArrowUp size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t('perfReview.questionnaires.down')}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      disabled={i === questions.length - 1}
                      onClick={() => move(i, 1)}
                      aria-label={t('perfReview.questionnaires.down')}
                    >
                      <IconArrowDown size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t('common.delete')}>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => onChange(questions.filter((_, j) => j !== i))}
                      aria-label={t('common.delete')}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              <Group gap="md" wrap="wrap">
                {isChoice ? (
                  // Вопросы с вариантами остались от встроенной анкеты: их можно
                  // переставлять и переименовывать, но варианты здесь не правятся.
                  <Badge variant="light" color="gray">
                    {t('perfReview.questionnaires.typeChoice')}
                  </Badge>
                ) : (
                  <SegmentedControl
                    size="xs"
                    value={q.type}
                    onChange={(v) =>
                      update(
                        i,
                        // Подписи точек заданы под старую шкалу — сбрасываем их.
                        v === 'scale'
                          ? {
                              type: 'scale',
                              scaleMin: 1,
                              scaleMax: q.scaleMax ?? 5,
                              scaleLabels: undefined,
                            }
                          : { type: 'text', scaleLabels: undefined },
                      )
                    }
                    data={[
                      { value: 'text', label: t('perfReview.questionnaires.typeText') },
                      { value: 'scale', label: t('perfReview.questionnaires.typeScale') },
                    ]}
                  />
                )}
                {/* Коллегам — только 1–5, как у блоков карты: их оценки усредняются вместе. */}
                {q.type === 'scale' && kind === 'self' && (
                  <SegmentedControl
                    size="xs"
                    value={String(q.scaleMax ?? 5)}
                    onChange={(v) =>
                      update(i, { scaleMin: 1, scaleMax: Number(v), scaleLabels: undefined })
                    }
                    data={[
                      { value: '5', label: '1–5' },
                      { value: '10', label: '1–10' },
                    ]}
                  />
                )}
                <Switch
                  size="xs"
                  label={t('perfReview.questionnaires.required')}
                  checked={!!q.required}
                  onChange={(e) => update(i, { required: e.currentTarget.checked })}
                />
                {kind === 'peer' && q.type === 'text' && (
                  <Switch
                    size="xs"
                    label={t('perfReview.questionnaires.allowDeanonymize')}
                    checked={!!q.allowDeanonymize}
                    onChange={(e) => update(i, { allowDeanonymize: e.currentTarget.checked })}
                  />
                )}
              </Group>
              {kind === 'peer' && (
                <TextInput
                  size="xs"
                  label={t('perfReview.questionnaires.block')}
                  placeholder={t('perfReview.questionnaires.blockPlaceholder')}
                  value={q.block ?? ''}
                  onChange={(e) => update(i, { block: e.currentTarget.value })}
                  maxLength={100}
                />
              )}
            </Stack>
          </Paper>
        );
      })}
      <Button
        variant="subtle"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={() => onChange([...questions, blankQuestion()])}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('perfReview.questionnaires.addQuestion')}
      </Button>
    </Stack>
  );
};
